import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
  Select,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { UserService, User } from '../../services/api/userService';
import { CompanyInstance, CompanyInstanceCreate } from '../../types/companyInstance';

const { Text } = Typography;
const { Option } = Select;

interface CompanyUsersTabProps {
  companyId: string;
}

export const CompanyUsersTab: React.FC<CompanyUsersTabProps> = ({ companyId }) => {
  const [instances, setInstances] = useState<CompanyInstance[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [instancesList, usersList] = await Promise.all([
        CompanyInstanceService.getAll({ company_id: companyId }),
        UserService.getAll(),
      ]);
      setInstances(instancesList);
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load company users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const createData: CompanyInstanceCreate = {
        company_id: companyId,
        user_id: values.user_id,
        instance_name: values.instance_name || `${values.user_id} Instance`,
        role: values.role || 'member',
        is_active: true,
      };

      await CompanyInstanceService.create(createData);
      message.success('User added to company successfully');
      setIsModalVisible(false);
      form.resetFields();
      await loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to add user to company');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (instanceId: string) => {
    try {
      setLoading(true);
      await CompanyInstanceService.delete(instanceId);
      message.success('User removed from company successfully');
      await loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to remove user from company');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableUsers = () => {
    const assignedUserIds = new Set(instances.map(inst => inst.user_id));
    return users.filter(user => !assignedUserIds.has(user.id));
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (instance: CompanyInstance) => {
        const user = users.find(u => u.id === instance.user_id);
        return user ? (
          <Space>
            <UserOutlined />
            <Text strong>{user.username || user.email}</Text>
            {user.email && <Text type="secondary">({user.email})</Text>}
          </Space>
        ) : (
          <Text type="secondary">{instance.user_id.substring(0, 8)}...</Text>
        );
      },
    },
    {
      title: 'Instance Name',
      dataIndex: 'instance_name',
      key: 'instance_name',
    },
    {
      title: 'Role',
      key: 'role',
      render: (instance: CompanyInstance) => {
        const user = users.find(u => u.id === instance.user_id);
        const role = user?.role;
        return role ? <Tag>{role}</Tag> : <Text type="secondary">No role</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'joined_at',
      key: 'joined_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (instance: CompanyInstance) => (
        <Popconfirm
          title="Are you sure you want to remove this user from the company?"
          description="This action cannot be undone."
          onConfirm={() => handleRemoveUser(instance.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            title="Remove user"
          >
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add User to Company
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={instances}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`,
        }}
      />

      <Modal
        title="Add User to Company"
        open={isModalVisible}
        onOk={handleAddUser}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        okText="Add"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select a user"
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {getAvailableUsers().map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.username || user.email} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="instance_name"
            label="Instance Name (Optional)"
          >
            <Input placeholder="Leave empty to auto-generate" />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="Role"
            initialValue="member"
          >
            <Select>
              <Option value="owner">Owner</Option>
              <Option value="admin">Admin</Option>
              <Option value="member">Member</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

